import { Box, TextField, InputLabel, Stack } from '@mui/material';
import dayjs from 'dayjs';
import React, { PropsWithChildren, ReactElement, ReactNode, useState, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';

export interface FormContactDatePickerProps<T extends object> {
  form: UseFormReturn<T>;
  label: ReactNode;
  fieldName: Path<T>;
  placeholder?: string;
  validation?: RegisterOptions;
}

const getMaxDaysInMonth = (month: number, year?: number) => {
  if (month < 1 || month > 12) return 31;

  // February - check for leap year
  if (month === 2) {
    if (!year) return 29; // Allow 29 for yearless dates
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return isLeap ? 29 : 28;
  }

  // Months with 30 days: April, June, September, November
  if ([4, 6, 9, 11].includes(month)) return 30;

  // Months with 31 days: January, March, May, July, August, October, December
  return 31;
};


export const FormContactDatePicker = <T extends FieldValues>(
  props: PropsWithChildren<FormContactDatePickerProps<T>>,
): ReactElement | null => {
  const { form, label, fieldName, validation } = props;

  return (
    <Controller
      control={form.control as Control<FieldValues>}
      name={fieldName}
      rules={validation}
        render={({ field: { onChange, value } }) => {
        const [month, setMonth] = useState<string>('');
        const [day, setDay] = useState<string>('');
        const [year, setYear] = useState<string>('');
        const [monthError, setMonthError] = useState<string>('');
        const [dayError, setDayError] = useState<string>('');
        const [yearError, setYearError] = useState<string>('');
        const [hasValidationError, setHasValidationError] = useState<boolean>(false);

        // Update form validation state when validation error state changes
        useEffect(() => {
          if (hasValidationError) {
            form.setError(fieldName, { type: 'manual', message: 'Invalid date' });
          } else {
            form.clearErrors(fieldName);
          }
        }, [hasValidationError, form, fieldName]);

        const validateFields = (newMonth: string, newDay: string, newYear: string) => {
          let monthErr = '';
          let dayErr = '';
          let yearErr = '';

          // Month validation
          if (newMonth && (parseInt(newMonth, 10) < 1 || parseInt(newMonth, 10) > 12)) {
            monthErr = '1-12';
          } else if (!newYear && newDay && !newMonth) {
            monthErr = '';
          }

          // Day validation
          if (newDay && newMonth) {
            const monthNum = parseInt(newMonth, 10);
            const dayNum = parseInt(newDay, 10);
            const yearNum = newYear ? parseInt(newYear, 10) : undefined;
            const maxDays = getMaxDaysInMonth(monthNum, yearNum);

            if (dayNum < 1 || dayNum > maxDays) {
              dayErr = `1-${maxDays}`;
            }
          } else if (!newYear && newMonth && !newDay) {
            dayErr = '';
          }

          // Year validation - always validate year if present, regardless of other fields
          if (newYear) {
            const yearNum = parseInt(newYear, 10);
            const currentYear = new Date().getFullYear();
            if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
              yearErr = `1900-${currentYear}`;
            }
          }

          // Check for valid date combinations to clear errors
          const hasValidCompleteDate = newMonth && newDay && newYear &&
            parseInt(newMonth, 10) >= 1 && parseInt(newMonth, 10) <= 12 &&
            parseInt(newDay, 10) >= 1 && parseInt(newDay, 10) <= getMaxDaysInMonth(parseInt(newMonth, 10), parseInt(newYear, 10)) &&
            parseInt(newYear, 10) >= 1900 && parseInt(newYear, 10) <= new Date().getFullYear();

          const hasValidYearlessDate = newMonth && newDay && !newYear &&
            parseInt(newMonth, 10) >= 1 && parseInt(newMonth, 10) <= 12 &&
            parseInt(newDay, 10) >= 1 && parseInt(newDay, 10) <= getMaxDaysInMonth(parseInt(newMonth, 10), undefined);

          if (hasValidCompleteDate || hasValidYearlessDate) {
            monthErr = '';
            dayErr = '';
            yearErr = '';
          }

          const newHasValidationError = monthErr !== '' || dayErr !== '' || yearErr !== '';
          setHasValidationError(newHasValidationError);
          setMonthError(monthErr);
          setDayError(dayErr);
          setYearError(yearErr);
        };

        useEffect(() => {
          if (value) {
            const isDateOnly = value.includes('T00:00:00.000Z');
            const date = isDateOnly ? dayjs(value.substring(0, 10)) : dayjs(value);
            if (date.isValid()) {
              const yearValue = date.year();
              const monthStr = String(date.month() + 1).padStart(2, '0');
              const dayStr = String(date.date()).padStart(2, '0');
              const yearStr = yearValue === 2400 ? '' : String(yearValue);
              setMonth(monthStr);
              setDay(dayStr);
              setYear(yearStr);
              validateFields(monthStr, dayStr, yearStr);
            }
          } else {
            setMonth('');
            setDay('');
            setYear('');
            validateFields('', '', '');
          }
        }, [value]);



        const updateFormValue = (newMonth: string, newDay: string, newYear: string) => {
          const monthNum = parseInt(newMonth, 10);
          const dayNum = parseInt(newDay, 10);
          const yearNum = newYear ? parseInt(newYear, 10) : null;

          const maxDays = getMaxDaysInMonth(monthNum, yearNum || undefined);

          // Allow updates only when:
          // 1. All three fields (month, day, year) are filled, OR
          // 2. Only month and day are filled (year is missing/empty)
          // Don't allow partial dates when year is missing
          if (
            (newMonth && newDay && newYear && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= maxDays) ||
            (newMonth && newDay && !newYear && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= maxDays)
          ) {
            const finalYear = yearNum && yearNum >= 1900 && yearNum <= 2100 ? yearNum : 2400;
            const dateString = `${finalYear}-${newMonth.padStart(2, '0')}-${newDay.padStart(2, '0')}`;
            onChange(dateString);
          } else if (!newMonth && !newDay && !newYear) {
            onChange('');
          }
          // For invalid combinations, don't update the form value - leave it as-is
        };

        const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let val = e.target.value.replace(/\D/g, '');
          if (val.length > 2) val = val.substring(0, 2);
          setMonth(val);
          validateFields(val, day, year);

          // For validation during typing, don't update form value yet
          if (val && val.length === 2) {
            const monthNum = parseInt(val, 10);
            if (monthNum >= 1 && monthNum <= 12) {
              // Adjust day if it's now invalid for the new month
              const dayNum = parseInt(day, 10);
              const yearNum = year ? parseInt(year, 10) : undefined;
              const maxDays = getMaxDaysInMonth(monthNum, yearNum);

              if (dayNum > maxDays) {
                const adjustedDay = String(maxDays).padStart(2, '0');
                setDay(adjustedDay);
                validateFields(val, adjustedDay, year);
                updateFormValue(val, adjustedDay, year);
              } else {
                updateFormValue(val, day, year);
              }
            }
          }
        };

        const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let val = e.target.value.replace(/\D/g, '');
          if (val.length > 2) val = val.substring(0, 2);
          setDay(val);
          validateFields(month, val, year);

          // For validation during typing, don't update form value yet
          if (val && val.length === 2 && month) {
            const monthNum = parseInt(month, 10);
            const dayNum = parseInt(val, 10);
            const yearNum = year ? parseInt(year, 10) : undefined;
            const maxDays = getMaxDaysInMonth(monthNum, yearNum);

            if (dayNum >= 1 && dayNum <= maxDays) {
              updateFormValue(month, val, year);
            }
          }
        };

        const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let val = e.target.value.replace(/\D/g, '');
          if (val.length > 4) val = val.substring(0, 4);
          setYear(val);

          // Adjust day if it's now invalid for February due to leap year change
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          const yearNum = val ? parseInt(val, 10) : undefined;
          const maxDays = getMaxDaysInMonth(monthNum, yearNum);

          if (dayNum > maxDays) {
            const adjustedDay = String(maxDays).padStart(2, '0');
            setDay(adjustedDay);
            updateFormValue(month, adjustedDay, val);
          } else {
            updateFormValue(month, day, val);
          }
        };

        const handleMonthBlur = () => {
          if (month && month.length === 1) {
            const formattedMonth = month.padStart(2, '0');
            setMonth(formattedMonth);
            validateFields(formattedMonth, day, year);
            updateFormValue(formattedMonth, day, year);
          } else {
            validateFields(month, day, year);
            updateFormValue(month, day, year);
          }
        };

        const handleDayBlur = () => {
          if (day && day.length === 1 && month) {
            const formattedDay = day.padStart(2, '0');
            setDay(formattedDay);
            validateFields(month, formattedDay, year);
            updateFormValue(month, formattedDay, year);
          } else {
            validateFields(month, day, year);
            updateFormValue(month, day, year);
          }
        };

        const handleYearBlur = () => {
          // Check if year is invalid and clear it if so
          if (year) {
            const yearNum = parseInt(year, 10);
            const currentYear = new Date().getFullYear();
            if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
              setYear('');
              validateFields(month, day, '');
              updateFormValue(month, day, '');
              return;
            }
          }

          validateFields(month, day, year);
          updateFormValue(month, day, year);
        };

        return (
          <Box sx={{ marginTop: '-5px' }}>
            <InputLabel sx={{ marginBottom: '1px', fontSize: '0.875rem' }}>{label}</InputLabel>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="MM"
                value={month}
                onChange={handleMonthChange}
                onBlur={handleMonthBlur}
                inputProps={{
                  maxLength: 2,
                  style: { textAlign: 'center' }
                }}
                sx={{ width: '30%' }}
                error={!!monthError}
                helperText={monthError}
              />
              <TextField
                placeholder="DD"
                value={day}
                onChange={handleDayChange}
                onBlur={handleDayBlur}
                inputProps={{
                  maxLength: 2,
                  style: { textAlign: 'center' }
                }}
                sx={{ width: '30%' }}
                error={!!dayError}
                helperText={dayError}
              />
              <TextField
                placeholder="YYYY"
                value={year}
                onChange={handleYearChange}
                onBlur={handleYearBlur}
                inputProps={{
                  maxLength: 4,
                  style: { textAlign: 'center' }
                }}
                sx={{ width: '40%' }}
                error={!!yearError}
                helperText={yearError}
              />
            </Stack>
          </Box>
        );
      }}
    />
  );
};


